import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import DatePicker from '@components/common/DatePicker';
import Select from '@components/common/Select';
import Button from '@components/common/Button';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import { clsx } from 'clsx';
import { FREQUENCY_LABELS } from '@utils/constants';
import { TrendingUp } from 'lucide-react';

const recurrenceSchema = z.object({
  description: z.string().min(2, 'Descrição obrigatória'),
  amount: z.number().positive('Valor deve ser positivo'),
  type: z.enum(['income', 'expense']),
  frequency: z.string().min(1, 'Frequência obrigatória'),
  accountId: z.string().min(1, 'Conta obrigatória'),
  categoryId: z.string().min(1, 'Categoria obrigatória'),
  startDate: z.string().min(1, 'Data de início obrigatória'),
  endDate: z.string().optional().nullable(),
  dayOfMonth: z.coerce.number().min(1).max(31).optional(),
  notes: z.string().optional(),
  remindInvest: z.boolean().default(false),
});

const frequencyOptions = Object.entries(FREQUENCY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const RecurrenceForm = ({ recurrence, onSubmit, onCancel, isLoading }) => {
  const { accounts } = useAccounts();
  const { incomeCategories, expenseCategories } = useCategories();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(recurrenceSchema),
    defaultValues: {
      description: '',
      amount: '',
      type: 'expense',
      frequency: 'monthly',
      accountId: '',
      categoryId: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      dayOfMonth: new Date().getDate(),
      notes: '',
      remindInvest: false,
    },
  });

  const selectedType = watch('type');
  const selectedFrequency = watch('frequency');
  const categories = selectedType === 'income' ? incomeCategories : expenseCategories;

  useEffect(() => {
    if (recurrence) {
      reset({
        description: recurrence.description,
        amount: Math.abs(recurrence.amount),
        type: recurrence.type,
        frequency: recurrence.frequency,
        accountId: recurrence.accountId,
        categoryId: recurrence.categoryId,
        startDate: recurrence.startDate,
        endDate: recurrence.endDate || '',
        dayOfMonth: recurrence.dayOfMonth || new Date().getDate(),
        notes: recurrence.notes || '',
        remindInvest: recurrence.remindInvest || false,
      });
    }
  }, [recurrence, reset]);

  useEffect(() => {
    if (!recurrence) {
      setValue('categoryId', '');
    }
  }, [selectedType, setValue, recurrence]);

  const handleFormSubmit = (data) => {
    onSubmit({
      ...data,
      endDate: data.endDate || null,
    });
  };

  const accountOptions = accounts.map(acc => ({
    value: acc.accountId,
    label: `${acc.icon} ${acc.name}`,
  }));

  const categoryOptions = categories.map(cat => ({
    value: cat.categoryId,
    label: `${cat.icon} ${cat.name}`,
  }));

  const showDayOfMonth = ['monthly', 'yearly'].includes(selectedFrequency);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Tipo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tipo
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setValue('type', 'expense')}
            className={clsx(
              'py-3 px-4 rounded-lg border-2 font-medium transition-all',
              selectedType === 'expense'
                ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/30'
                : 'border-gray-200 dark:border-gray-700 text-gray-600'
            )}
          >
            💸 Despesa Fixa
          </button>
          <button
            type="button"
            onClick={() => setValue('type', 'income')}
            className={clsx(
              'py-3 px-4 rounded-lg border-2 font-medium transition-all',
              selectedType === 'income'
                ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/30'
                : 'border-gray-200 dark:border-gray-700 text-gray-600'
            )}
          >
            💰 Receita Fixa
          </button>
        </div>
      </div>

      <Input
        label="Descrição"
        placeholder="Ex: Aluguel, Salário, Netflix..."
        error={errors.description?.message}
        {...register('description')}
      />

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="amount"
          control={control}
          render={({ field }) => (
            <CurrencyInput
              label="Valor"
              value={field.value}
              onValueChange={(values) => field.onChange(values.floatValue ?? '')}
              onBlur={field.onBlur}
              error={errors.amount?.message}
            />
          )}
        />

        <Select
          label="Frequência"
          options={frequencyOptions}
          error={errors.frequency?.message}
          {...register('frequency')}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <DatePicker
          label="Data de Início"
          error={errors.startDate?.message}
          {...register('startDate')}
        />

        {showDayOfMonth && (
          <Input
            label="Dia do Vencimento"
            type="number"
            min="1"
            max="31"
            error={errors.dayOfMonth?.message}
            {...register('dayOfMonth')}
          />
        )}
      </div>

      <Select
        label="Conta"
        options={accountOptions}
        placeholder="Selecione uma conta"
        error={errors.accountId?.message}
        {...register('accountId')}
      />

      <Select
        label="Categoria"
        options={categoryOptions}
        placeholder="Selecione uma categoria"
        error={errors.categoryId?.message}
        {...register('categoryId')}
      />

      <DatePicker
        label="Data de Término (opcional)"
        hint="Deixe em branco para recorrência sem fim"
        {...register('endDate')}
      />

      <Input
        label="Observações (opcional)"
        placeholder="Adicione uma nota..."
        {...register('notes')}
      />

      {selectedType === 'income' && (
        <label className={clsx(
          'flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all',
          watch('remindInvest')
            ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
        )}>
          <input
            type="checkbox"
            className="hidden"
            {...register('remindInvest')}
          />
          <div className={clsx(
            'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
            watch('remindInvest')
              ? 'bg-emerald-100 dark:bg-emerald-900/40'
              : 'bg-gray-100 dark:bg-gray-700'
          )}>
            <TrendingUp size={18} className={watch('remindInvest') ? 'text-emerald-600' : 'text-gray-400'} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Lembrar de investir ao receber
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              A cada competencia processada, o MyBank calcula o quanto sobra e envia um alerta automatico
            </p>
          </div>
        </label>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {recurrence ? 'Salvar' : 'Criar Recorrência'}
        </Button>
      </div>
    </form>
  );
};

export default RecurrenceForm;
